export function initSaunaAccordions(root = document) {
  root.querySelectorAll('.cpg-section-wrapper .cpg-accordion-trigger').forEach((button) => {
    if (button.dataset.msSaunaAriaBound === 'true') return;
    button.dataset.msSaunaAriaBound = 'true';
    button.setAttribute('aria-expanded', button.classList.contains('open') ? 'true' : 'false');
    button.addEventListener('click', () => queueMicrotask(() => {
      button.setAttribute('aria-expanded', button.classList.contains('open') ? 'true' : 'false');
    }));
  });
}

const saunaJourneyLifecycle = new WeakMap();
const saunaFeatureLifecycle = new WeakMap();
const saunaVideoLifecycle = new WeakMap();

function elementsIn(root, selector) {
  const elements = Array.from(root.querySelectorAll?.(selector) ?? []);
  if (root.matches?.(selector)) elements.unshift(root);
  return [...new Set(elements)];
}

function setJourneyActive(pairs, moments, scenes, progress, activeIndex) {
  pairs.forEach((pair, index) => {
    pair.setAttribute('data-active', String(index === activeIndex));
  });

  moments.forEach((moment, index) => {
    const isActive = index === activeIndex;
    moment.setAttribute('data-active', String(isActive));
    if (isActive) moment.setAttribute('aria-current', 'step');
    else moment.removeAttribute('aria-current');

    const scene = scenes[index];
    if (!scene) return;
    scene.setAttribute('data-active', String(isActive));
    scene.setAttribute('aria-hidden', String(!isActive));
  });

  if (progress) progress.textContent = String(activeIndex + 1).padStart(2, '0');
}

export function initSaunaJourneys(root = document) {
  elementsIn(root, '[data-ms-sauna-journey]').forEach((journey) => {
    if (saunaJourneyLifecycle.has(journey)) return;

    const pairs = Array.from(journey.querySelectorAll('[data-ms-sauna-pair]'));
    const moments = Array.from(journey.querySelectorAll('[data-ms-sauna-moment]'));
    const scenes = Array.from(journey.querySelectorAll('[data-ms-sauna-scene]'));
    const triggers = Array.from(journey.querySelectorAll('[data-ms-sauna-trigger]'));
    const progress = journey.querySelector?.('[data-ms-sauna-progress-current]') ?? null;
    if (moments.length === 0 || pairs.length !== moments.length || scenes.length !== moments.length) return;

    journey.dataset.msSaunaJourneyBound = 'true';
    setJourneyActive(pairs, moments, scenes, progress, 0);

    if (!('IntersectionObserver' in window) || triggers.length !== moments.length) {
      saunaJourneyLifecycle.set(journey, { observer: null });
      return;
    }

    journey.dataset.msSaunaJourneyEnhanced = 'true';
    const visibleRatios = new Map(triggers.map((trigger) => [trigger, 0]));
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        visibleRatios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
      });

      let bestIndex = -1;
      let bestRatio = 0;
      triggers.forEach((trigger, index) => {
        const ratio = visibleRatios.get(trigger) ?? 0;
        if (ratio > bestRatio) {
          bestIndex = index;
          bestRatio = ratio;
        }
      });
      if (bestIndex >= 0) setJourneyActive(pairs, moments, scenes, progress, bestIndex);
    }, { rootMargin: '-45% 0px -45% 0px', threshold: [0, 0.01, 0.5, 1] });

    triggers.forEach((trigger) => observer.observe(trigger));
    saunaJourneyLifecycle.set(journey, { observer });
  });
}

export function cleanupSaunaJourneys(root = document) {
  elementsIn(root, '[data-ms-sauna-journey]').forEach((journey) => {
    saunaJourneyLifecycle.get(journey)?.observer?.disconnect();
    saunaJourneyLifecycle.delete(journey);
    delete journey.dataset.msSaunaJourneyBound;
    delete journey.dataset.msSaunaJourneyEnhanced;
  });
}

function videosIn(root) {
  return elementsIn(root, '[data-ms-sauna-video]');
}

function featureSectionsIn(root) {
  return elementsIn(root, '[data-ms-sauna-features]');
}

function removeFeatureMedia(video) {
  const media = video.closest('.ms-sauna-feature__media');
  const feature = video.closest('[data-ms-sauna-feature]');
  media?.remove();
  feature?.classList.add('ms-sauna-feature--text-only');
}

function replaceWithFallbackImage(video, source, stage) {
  if (!source) {
    removeFeatureMedia(video);
    return;
  }

  const image = document.createElement('img');
  image.className = 'ms-sauna-feature__image';
  image.src = source;
  image.alt = video.dataset.msSaunaMediaAlt || '';
  image.loading = 'lazy';
  image.dataset.msSaunaFallbackStage = stage;
  image.addEventListener('error', () => {
    const secondarySource = video.dataset.msSaunaImageSrc;
    if (image.dataset.msSaunaFallbackStage === 'poster' && secondarySource && image.src !== secondarySource) {
      image.dataset.msSaunaFallbackStage = 'image';
      image.src = secondarySource;
      return;
    }
    image.closest('.ms-sauna-feature__media')?.remove();
    image.closest('[data-ms-sauna-feature]')?.classList.add('ms-sauna-feature--text-only');
  }, { once: false });
  video.replaceWith(image);
}

function cleanupSaunaVideo(video) {
  const lifecycle = saunaVideoLifecycle.get(video);
  lifecycle?.observer?.disconnect();
  if (lifecycle?.onError) video.removeEventListener('error', lifecycle.onError);
  saunaVideoLifecycle.delete(video);
  delete video.dataset.msSaunaVideoBound;
  video.pause();
}

function safelyPlay(video) {
  const playPromise = video.play();
  if (playPromise?.catch) playPromise.catch(() => {});
}

function shouldPlayVideo(video) {
  const videoState = saunaVideoLifecycle.get(video);
  const featureState = videoState && saunaFeatureLifecycle.get(videoState.section);
  return Boolean(
    videoState
    && featureState
    && videoState.visible
    && !document.hidden
    && !isFeatureMotionPaused(featureState)
  );
}

function updateVideoPlayback(video) {
  if (shouldPlayVideo(video)) safelyPlay(video);
  else video.pause();
}

function updateFeaturePlayback(section) {
  const lifecycle = saunaFeatureLifecycle.get(section);
  lifecycle?.videos.forEach((video) => updateVideoPlayback(video));
}

function isFeatureMotionPaused(lifecycle) {
  return lifecycle.motionPausedOverride ?? lifecycle.reducedMotion;
}

function updateMotionControl(section) {
  const lifecycle = saunaFeatureLifecycle.get(section);
  if (!lifecycle) return;
  const isPaused = isFeatureMotionPaused(lifecycle);
  const label = isPaused ? lifecycle.button?.dataset.labelResume : lifecycle.button?.dataset.labelPause;
  section.setAttribute('data-ms-sauna-motion-paused', String(isPaused));
  if (lifecycle.button) {
    lifecycle.button.setAttribute('aria-pressed', String(isPaused));
    lifecycle.button.setAttribute('aria-label', label);
    lifecycle.button.textContent = label;
  }
}

function setFeatureMotionPaused(section, paused) {
  const lifecycle = saunaFeatureLifecycle.get(section);
  if (!lifecycle) return;
  lifecycle.motionPausedOverride = Boolean(paused);
  updateMotionControl(section);
  updateFeaturePlayback(section);
}

function initSaunaFeatureVideos(section) {
  if (saunaFeatureLifecycle.has(section)) return;

  const videos = videosIn(section);
  const button = section.querySelector?.('[data-ms-sauna-motion-toggle]') ?? null;
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const lifecycle = {
    button,
    motionQuery,
    motionPausedOverride: null,
    reducedMotion: motionQuery.matches,
    videos,
  };
  saunaFeatureLifecycle.set(section, lifecycle);
  section.dataset.msSaunaFeaturesBound = 'true';

  lifecycle.onVisibilityChange = () => updateFeaturePlayback(section);
  lifecycle.onMotionChange = (event) => {
    lifecycle.reducedMotion = event.matches;
    updateMotionControl(section);
    updateFeaturePlayback(section);
  };
  lifecycle.onToggle = () => setFeatureMotionPaused(section, !isFeatureMotionPaused(lifecycle));
  document.addEventListener('visibilitychange', lifecycle.onVisibilityChange);
  motionQuery.addEventListener?.('change', lifecycle.onMotionChange);
  button?.addEventListener('click', lifecycle.onToggle);
  updateMotionControl(section);

  videos.forEach((video) => {
    video.dataset.msSaunaVideoBound = 'true';
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.autoplay = true;
    video.playsInline = true;

    const onError = () => {
      lifecycle.videos = lifecycle.videos.filter((item) => item !== video);
      cleanupSaunaVideo(video);
      replaceWithFallbackImage(video, video.dataset.msSaunaPosterSrc || video.dataset.msSaunaImageSrc, video.dataset.msSaunaPosterSrc ? 'poster' : 'image');
    };
    video.addEventListener('error', onError);

    const state = {
      section,
      onError,
      observer: null,
      visible: false,
    };
    saunaVideoLifecycle.set(video, state);

    if ('IntersectionObserver' in window) {
      state.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          state.visible = entry.isIntersecting && entry.intersectionRatio > 0;
          updateVideoPlayback(video);
        });
      }, { rootMargin: '20% 0px', threshold: [0, 0.01, 0.25] });
      state.observer.observe(video);
    }
    updateVideoPlayback(video);
  });
}

export function initSaunaVideos(root = document) {
  featureSectionsIn(root).forEach(initSaunaFeatureVideos);
}

export function setSaunaMotionPaused(root = document, paused = true) {
  initSaunaVideos(root);
  featureSectionsIn(root).forEach((section) => setFeatureMotionPaused(section, paused));
}

function cleanupSaunaFeatureVideos(section) {
  const lifecycle = saunaFeatureLifecycle.get(section);
  if (!lifecycle) return;
  document.removeEventListener('visibilitychange', lifecycle.onVisibilityChange);
  lifecycle.motionQuery.removeEventListener?.('change', lifecycle.onMotionChange);
  lifecycle.button?.removeEventListener('click', lifecycle.onToggle);
  lifecycle.videos.forEach(cleanupSaunaVideo);
  saunaFeatureLifecycle.delete(section);
  delete section.dataset.msSaunaFeaturesBound;
  section.removeAttribute('data-ms-sauna-motion-paused');
}

export function cleanupSaunaVideos(root = document) {
  featureSectionsIn(root).forEach(cleanupSaunaFeatureVideos);
  videosIn(root).forEach((video) => {
    if (saunaVideoLifecycle.has(video)) cleanupSaunaVideo(video);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initSaunaAccordions();
  initSaunaJourneys();
  initSaunaVideos();
});
document.addEventListener('shopify:section:load', (event) => {
  initSaunaAccordions(event.target);
  initSaunaJourneys(event.target);
  initSaunaVideos(event.target);
});
document.addEventListener('shopify:section:unload', (event) => {
  cleanupSaunaJourneys(event.target);
  cleanupSaunaVideos(event.target);
});

